import styles from './style.postcss';

import React, { Component } from 'react';
import { List, Map } from 'immutable';
import { connect, testClass, PageCard, ProductionStatus } from 'omni-common-ui';
import { bindActionCreators } from 'redux';
import PropTypes from 'prop-types';
import ExpansionButton from 'components/ExpansionButton';
import {
  TableView, TableViewTr, MemberCardTd,
  NormalTd, HeaderTd,
} from 'components/TableView';
import { actions as GroupMembers } from 'data/GroupMembers';
import {
  actions as RAv2HomeworkResultSummaryActions,
  accessors as RAv2HomeworkResultSummaryAccessors,
} from 'data/RAv2HomeworkResultSummary';
import { parseProductionStatus } from 'domain/ProductionStatus';

const INITIAL_VIEW_COUNT = 2;

function getStudentSummary(homeworkSummary, studentId) {
  if (! homeworkSummary || ! Map.isMap(homeworkSummary)) {
    return {};
  }
  return homeworkSummary.get(`${studentId}`) || {};
}

const COLUMNS = [
  {
    key: 'name',
    text: 'Name',
    sortable: true,
    renderCell: (member) => (
      <MemberCardTd
          key="name"
          name={`${member.studentGeneralInfo.firstName} ${member.studentGeneralInfo.lastName}`}
          surname={member.studentGeneralInfo.localName}
          avatarUrl={member.studentGeneralInfo.avatarUrl}
          gender={member.studentGeneralInfo.gender} />
    ),
  },
  {
    key: 'status',
    text: 'Status',
    sortable: false,
    renderCell: (member) => {
      const { isInProduction } = member;
      const status = Object.assign({ isInProduction }, parseProductionStatus(member.status));
      return (
        <NormalTd key="status">
          <ProductionStatus status={status.fullName} highlighted={status.isInProduction} />
        </NormalTd>
      );
    },
  },
  {
    key: 'avgCompletion',
    text: 'Avg. Completion',
    sortable: true,
    renderCell: (member, homeworkSummary) => {
      const summary = getStudentSummary(homeworkSummary, member.studentGeneralInfo.id);
      return <NormalTd key="avgCompletion">{`${summary.avgCompletion || 0}%`}</NormalTd>;
    },
  },
  {
    key: 'avgAccuracy',
    text: 'Avg. Accuracy',
    sortable: true,
    renderCell: (member, homeworkSummary) => {
      const summary = getStudentSummary(homeworkSummary, member.studentGeneralInfo.id);
      return <NormalTd key="avgAccuracy">{`${summary.avgAccuracy || 0}%`}</NormalTd>;
    },
  },
  {
    key: 'totalRecorded',
    text: 'Total Recorded',
    sortable: true,
    renderCell: (member, homeworkSummary) => {
      const summary = getStudentSummary(homeworkSummary, member.studentGeneralInfo.id);
      return <NormalTd key="totalRecorded">{summary.totalRecorded || 0}</NormalTd>;
    },
  },
  {
    key: 'totalStoryRead',
    text: 'Total Story Read',
    sortable: true,
    renderCell: (member, homeworkSummary) => {
      const summary = getStudentSummary(homeworkSummary, member.studentGeneralInfo.id);
      return <NormalTd key="totalStoryRead">{summary.totalStoryRead || 0}</NormalTd>;
    },
  },
];

class PerformanceCardRAv2 extends Component {
  constructor(props) {
    super(props);
    this.state = {
      isExpand: false,
      sortKey: 'name',
      sortType: 'Ascending',
    };
  }

  componentDidMount() {
    const { groupId } = this.props;
    if (groupId) {
      this.props.fetchRAv2HomeworkResultSummaryIfNeeded(groupId);
    }
  }

  _sort(sortKey) {
    let sortType = 'Ascending';
    if (sortKey === this.state.sortKey) {
      sortType = this.state.sortType === 'Ascending' ? 'Descending' : 'Ascending';
    }
    this.setState({ sortKey, sortType });
  }

  _getSortedMembers() {
    const { members } = this.props;
    if (! members || members.size === 0) return new List();

    return members.sort((a, b) => {
      const { sortKey, sortType } = this.state;
      let current;
      let next;

      switch (sortKey) {
        case 'name':
          current = `${a.studentGeneralInfo.firstName} ${a.studentGeneralInfo.lastName}`.toLowerCase();
          next = `${b.studentGeneralInfo.firstName} ${b.studentGeneralInfo.lastName}`.toLowerCase();
          break;
        default: {
          const { homeworkSummary } = this.props;
          const summaryA = getStudentSummary(homeworkSummary, a.studentGeneralInfo.id);
          const summaryB = getStudentSummary(homeworkSummary, b.studentGeneralInfo.id);
          current = summaryA[sortKey] || 0;
          next = summaryB[sortKey] || 0;
          break;
        }
      }

      if (current > next) return sortType === 'Ascending' ? 1 : -1;
      if (current < next) return sortType === 'Ascending' ? -1 : 1;
      return 0;
    });
  }

  _renderTableHeaderRow() {
    const { sortKey, sortType } = this.state;
    return (
      <TableViewTr key="PerformanceCardRAv2-header">
        {COLUMNS.map((col) => (
          <HeaderTd
              sortable={col.sortable}
              sortType={sortKey === col.key ? sortType : ''}
              onSort={() => this._sort(col.key)}
              key={col.key}>
            {col.text}
          </HeaderTd>
        ))}
      </TableViewTr>
    );
  }

  _renderTableRow(member) {
    const { homeworkSummary } = this.props;
    return (
      <TableViewTr key={`${member.studentGeneralInfo.id}-rav2`}>
        {COLUMNS.map((col) => col.renderCell(member, homeworkSummary))}
      </TableViewTr>
    );
  }

  _renderExpansionButton(totalSize) {
    if (totalSize <= INITIAL_VIEW_COUNT) return null;
    return (
      <ExpansionButton
          isExpand={this.state.isExpand}
          onExpansionButtonClick={() => this.setState({ isExpand: ! this.state.isExpand })} />
    );
  }

  render() {
    const sorted = this._getSortedMembers();
    const source = this.state.isExpand
      ? sorted
      : sorted.slice(0, INITIAL_VIEW_COUNT);

    return (
      <PageCard className={testClass('performance-card-rav2')}>
        <PageCard.Heading
            text="Performance"
            className={styles.PerformanceCardRAv2_heading}>
          <div className={styles.PerformanceCardRAv2_infoIcon}>
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M11 7H13V9H11V7ZM11 11H13V17H11V11ZM12 2C6.48 2 2 6.48 2 12C2 17.52 6.48 22 12 22C17.52 22 22 17.52 22 12C22 6.48 17.52 2 12 2ZM12 20C7.59 20 4 16.41 4 12C4 7.59 7.59 4 12 4C16.41 4 20 7.59 20 12C20 16.41 16.41 20 12 20Z" fill="black" />
            </svg>
            <div className={styles.PerformanceCardRAv2_tooltip}>
              <h3 className={styles.PerformanceCardRAv2_tooltip_title}>Performance Legends</h3>
              <div className={styles.PerformanceCardRAv2_tooltip_item}>
                <strong>Completion</strong>
                <p>Completion rate records include completing Step2 activities</p>
              </div>
              <div className={styles.PerformanceCardRAv2_tooltip_item}>
                <strong>Accuracy</strong>
                <p>Accuracy rate is calculated based on the ratio of correct answers to total answers in the step 2 activities</p>
              </div>
              <div className={styles.PerformanceCardRAv2_tooltip_item}>
                <strong>Story Read</strong>
                <p>Reading completion is marked as achieved when the student listen to and read the entire story once</p>
              </div>
              <div className={styles.PerformanceCardRAv2_tooltip_item}>
                <strong>Recorded</strong>
                <p>Recordings are displayed based on how many times a student records the story, with each attempt contributing to the total count</p>
              </div>
            </div>
          </div>
        </PageCard.Heading>
        <PageCard.Content className={styles.PerformanceCardRAv2_content}>
          <TableView
              source={source.toArray()}
              renderTableRow={(row) => this._renderTableRow(row)}
              noResultsString="No Results."
              query={{}}
              customerHeader={this._renderTableHeaderRow()}
              customerFooter={this._renderExpansionButton(sorted.size)} />
        </PageCard.Content>
      </PageCard>
    );
  }
}

PerformanceCardRAv2.propTypes = {
  members: PropTypes.object,
  homeworkSummary: PropTypes.object,
  groupId: PropTypes.string,
  fetchRAv2HomeworkResultSummaryIfNeeded: PropTypes.func.isRequired,
};

function mapStateToProps(state, ownProps) {
  const { groupId } = ownProps.params;
  const members = GroupMembers.getMembersByGroup(state, groupId);
  const homeworkSummary = RAv2HomeworkResultSummaryAccessors
    .getRAv2HomeworkResultSummaryByGroup(state, groupId);
  return { members, homeworkSummary, groupId };
}

function mapDispatchToProps(dispatch) {
  return bindActionCreators({
    fetchRAv2HomeworkResultSummaryIfNeeded:
      RAv2HomeworkResultSummaryActions.fetchRAv2HomeworkResultSummaryIfNeeded,
  }, dispatch);
}

export default connect(mapStateToProps, mapDispatchToProps)(PerformanceCardRAv2);


// WEBPACK FOOTER //
// ./containers/Group/Dashboard/PerformanceCardRAv2/index.jsx